import { useState, useRef, useCallback, useEffect } from 'react';
import { STREAM_DURATION_DEFAULTS } from '../utils/cameraConstants';

const {
  defaultMinutes,
  warningBeforeSeconds,
  extendMinutes,
  localStorageKey,
} = STREAM_DURATION_DEFAULTS;

/**
 * useStreamTimer - Per-channel stream duration limiter
 *
 * Manages countdown timers for each active camera channel to prevent
 * M2M data plan exhaustion. Warns before timeout and allows extension.
 *
 * @param {Object} options
 * @param {function} options.onTimeout - Called with (channel) when timer expires
 * @param {function} options.onWarning - Called with (channel) when warning threshold reached
 * @returns {Object} Timer controls and state
 */
const useStreamTimer = ({ onTimeout, onWarning } = {}) => {
  const onTimeoutRef = useRef(onTimeout);
  const onWarningRef = useRef(onWarning);
  onTimeoutRef.current = onTimeout;
  onWarningRef.current = onWarning;

  // Duration setting (persisted in localStorage)
  const [durationMinutes, setDurationMinutesState] = useState(() => {
    const saved = localStorage.getItem(localStorageKey);
    return saved ? Number(saved) : defaultMinutes;
  });

  // Force re-render on each tick so getRemainingTime returns fresh values
  const [, setTick] = useState(0);

  // Map<channel, { endTime: number, warned: boolean }>
  const timersRef = useRef(new Map());
  const intervalRef = useRef(null);

  const setDurationMinutes = useCallback((minutes) => {
    setDurationMinutesState(minutes);
    localStorage.setItem(localStorageKey, String(minutes));
  }, []);

  // Ref to keep durationMinutes accessible in callbacks without stale closure
  const durationMinutesRef = useRef(durationMinutes);
  durationMinutesRef.current = durationMinutes;

  const startTimer = useCallback((channel) => {
    // Sync: if other channels already have timers, inherit the earliest endTime
    // so all channels expire together (session-style limit)
    let endTime;
    if (timersRef.current.size > 0) {
      let earliest = Infinity;
      timersRef.current.forEach((timer) => {
        if (timer.endTime < earliest) earliest = timer.endTime;
      });
      endTime = earliest;
    } else {
      endTime = Date.now() + durationMinutesRef.current * 60 * 1000;
    }
    // If already within warning range, mark warned to avoid duplicate warnings
    const remainingSec = (endTime - Date.now()) / 1000;
    timersRef.current.set(channel, {
      endTime,
      warned: remainingSec <= warningBeforeSeconds,
    });
  }, []);

  const stopTimer = useCallback((channel) => {
    timersRef.current.delete(channel);
  }, []);

  const extendTimer = useCallback((channel) => {
    const timer = timersRef.current.get(channel);
    if (timer) {
      timer.endTime += extendMinutes * 60 * 1000;
      timer.warned = false;
    }
  }, []);

  const getRemainingTime = useCallback((channel) => {
    const timer = timersRef.current.get(channel);
    if (!timer) return null;
    return Math.max(0, Math.round((timer.endTime - Date.now()) / 1000));
  }, []);

  const getWarningState = useCallback((channel) => {
    const remaining = getRemainingTime(channel);
    return remaining !== null && remaining <= warningBeforeSeconds && remaining > 0;
  }, [getRemainingTime]);

  // Tick interval — checks all timers every second
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      const now = Date.now();
      let anyActive = false;
      const expired = [];

      let firstWarning = null;
      timersRef.current.forEach((timer, channel) => {
        anyActive = true;
        const remainingMs = timer.endTime - now;
        const remainingSec = remainingMs / 1000;

        if (remainingSec <= 0) {
          expired.push(channel);
        } else if (remainingSec <= warningBeforeSeconds && !timer.warned) {
          timer.warned = true;
          // Only fire one warning callback per tick (avoid snackbar flicker)
          if (!firstWarning) firstWarning = channel;
        }
      });

      // Fire single warning for the tick (CameraViewPage handles all channels)
      if (firstWarning && onWarningRef.current) {
        onWarningRef.current(firstWarning);
      }

      // Handle expired timers
      expired.forEach((channel) => {
        timersRef.current.delete(channel);
        if (onTimeoutRef.current) onTimeoutRef.current(channel);
      });

      // Trigger re-render so getRemainingTime gives fresh values to UI
      if (anyActive || expired.length > 0) {
        setTick((t) => t + 1);
      }
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Cleanup all timers on unmount
  useEffect(() => () => {
    timersRef.current.clear();
  }, []);

  return {
    startTimer,
    stopTimer,
    extendTimer,
    getRemainingTime,
    getWarningState,
    durationMinutes,
    setDurationMinutes,
  };
};

export default useStreamTimer;
